import { useRef, type ReactNode, type RefObject } from 'react';
import {
  CalendarDotsIcon,
  MagnifyingGlassIcon,
  MapPinIcon,
  UsersIcon,
} from '@phosphor-icons/react';
import { Button } from '@voll-intelligence/ui-kit/ui/button';
import { CommandList } from '@voll-intelligence/ui-kit/ui/command';
import { PlaceField, PlaceSearchInput } from '../../shared/place_search/place_field.js';
import { MyLocationRow } from '../../shared/place_search/my_location_row.js';
import { PlaceCommand, PlaceResults } from '../../shared/place_search/place_panel.js';
import { PageFieldSheet } from '../../shared/page_field_sheet.js';
import { returnFocusUnlessMoved } from '../../shared/return_focus.js';
import { useMediaQuery } from '@voll-intelligence/ui-kit/hooks/use-media-query';
import type {
  HotelAsyncState,
  HotelPlaceSuggestion,
  HotelSearchValue,
  HotelTravelerOption,
} from '../models.js';
import type { HotelDateCopy } from './hotel_date_calendar.js';
import { StayCalendar } from './stay_calendar.js';
import { searchCta, staySearches, useStayConfirm } from './hotel_stay_confirm.js';
import {
  HotelRoomsField,
  type HotelRoomsFieldCopy,
  type HotelRoomsFieldProps,
} from './hotel_rooms_field.js';
import { HotelRoomsPickerField } from './hotel_rooms_picker_field.js';
import { commitPlace, namedGuestCount, pendingHotelSearchFields } from './hotel_search_pending.js';
import {
  guidedField,
  useHotelSearchPanels,
  usePlaceDraft,
  type HotelSearchPanels,
} from './hotel_search_panels.js';
import { SearchField } from '../../shared/search_field.js';
import { guestGlyph } from './hotel_search_field.js';
import { HOTEL_NARROW_QUERY } from './hotel_narrow.js';
import {
  HotelSearchSheet,
  HotelSearchSummary,
  type HotelSearchSheetCard,
} from './hotel_search_sheet.js';
import './hotel_search_bar.css';

type HotelSearchCopyBase = HotelRoomsFieldCopy & HotelDateCopy;

export type HotelSearchCopy = HotelSearchCopyBase & {
  searchLabel: string;
  destinationLabel: string;
  destinationPlaceholder: string;
  /** Ação "perto de mim" — primeira linha do popover de destino. */
  myLocationLabel: string;
  /** Permissão negada / geolocation indisponível: dica na própria linha. */
  myLocationErrorLabel: string;
  datesLabel: string;
  /* Os três rótulos VISÍVEIS do layout de página (`naked`): a PERGUNTA de cada
     campo, não o assunto dele. Convivem com `destinationLabel`/`datesLabel`/
     `roomsLabel`, que seguem nomeando os painéis. */
  whoLabel: string;
  whereLabel: string;
  whenLabel: string;
  /** A garantia de que a política da empresa já vale. NÃO É RENDERIZADA hoje
   *  (#2655): a linha sai das telas de busca até existir a modal de políticas
   *  que ela promete abrir; a copy fica para a volta, então o campo segue
   *  opcional em vez de sumir do contrato. */
  policyHintLabel?: string;
  /** Título da folha do palco estreito ("Ajustar busca") — a mesma frase que o
   *  resultado já usa pra levar ao formulário. */
  adjustSearchLabel: string;
  noPlacesLabel: string;
  placeErrorLabel: string;
  /** O Confirmar das datas quando ele também BUSCA ("Confirmar e buscar"). */
  confirmAndSearchLabel: string;
  datesEmptyLabel: string;
  /* O QUANDO vazio no layout de página. Separado de `datesEmptyLabel`: na
     pílula estreita do card cabe "A definir"; na página o campo tem 208px e o
     desenho pede os dois marcos por extenso. */
  datesPlaceholder: string;
};

export type HotelSearchBarProps = {
  value: HotelSearchValue;
  copy: HotelSearchCopy;
  dateSummary: string;
  dateAccessibleSummary?: string;
  requesterId?: string;
  travelers: readonly HotelTravelerOption[];
  placeSuggestions: readonly HotelPlaceSuggestion[];
  placeSearchState: HotelAsyncState;
  travelerSearchState: HotelAsyncState;
  hasMoreTravelers?: boolean;
  readOnly?: boolean;
  busy?: boolean;
  /** A régua do host de "pode buscar" para um valor — o Buscar trava por ela,
   *  e o Confirmar das datas a pergunta sobre o valor NOVO. Ausente ⇒ sempre. */
  canSubmit?(value: HotelSearchValue): boolean;
  /** Neste palco o submit não BUSCA: ele escreve na conversa (card superado —
   *  refinar deixa de ser leitura in-place e vira turno). Enviar mensagem é
   *  gesto para fora e não pode sair de confirmar um campo, então o Confirmar
   *  do calendário só entrega as datas aqui. A lupa segue fazendo o turno para
   *  quem quiser. Ausente ⇒ falso: a página, a bancada e o card corrente
   *  buscam de verdade. */
  submitIsTurn?: boolean;
  /** Layout de PÁGINA (o passo de busca determinístico): a pílula-card sai de
   *  cena, cada campo ganha o rótulo da pergunta em cima, o Buscar mostra o
   *  próprio nome e a garantia de política fecha o bloco. Ausente ⇒ a barra do
   *  card/mapa da conversa, que é o default. */
  naked?: boolean;
  /** O preenchimento GUIADO: o primeiro campo vazio pede a vez (o anel de
   *  pendente) e ESCOLHER dentro de um painel abre o próximo campo pendente da
   *  fileira — Quem → Onde → Quando, a ordem da tela — em popover na fileira larga (página e
   *  cápsula do card da conversa) e em folha na página estreita; com tudo
   *  respondido o foco pousa no Buscar. Só a continuação de um gesto abre —
   *  apagar ou trocar um campo já respondido só move o anel, e o Confirmar das
   *  datas segue buscando quando nada mais falta —, salvo ao nascer, com o
   *  primeiro campo pendente já aberto (ver `guidedOpen`). Na
   *  folha compacta da conversa (pílula + três cartões) o pick só grava — ver
   *  `SheetPlaceSearch`. Quem liga no card é o organismo do hotel, só no
   *  passo de busca e enquanto o card é o último da conversa. Ausente ⇒ nada
   *  disso. */
  guided?: boolean;
  /** Com `guided`, onde o primeiro campo pendente já nasce aberto (o Onde com o
   *  input focado) — ver `GuidedOpen`. A página omite (só na fileira larga); o
   *  card da conversa passa `always` (também na folha compacta) ou `never` (o
   *  card remontado do histórico, cujo gesto já foi gasto). */
  guidedOpen?: Parameters<typeof guidedField>[0]['guidedOpen'];
  onChange(value: HotelSearchValue): void;
  /** A busca, com o valor que ela leva — do Buscar ou do Confirmar das datas,
   *  que sai antes do commit do `onChange`. */
  onSubmit(value: HotelSearchValue): void;
  onPlaceQueryChange(query: string): void;
  onTravelerQueryChange(query: string): void;
  onLoadMoreTravelers(): void;
  /** Estado da leitura de geolocation, do host. Ausente junto de
   *  `onUseMyLocation` ⇒ a linha "perto de mim" não existe. */
  myLocation?: { state: 'idle' | 'loading' | 'error' };
  /** O host grava o lugar da posição com `placeSource: 'geolocation'`; um pick
   *  da barra apaga a origem. */
  onUseMyLocation?(): void;
  /** Toda escolha na lista do Onde, no desktop e na folha do celular, mesmo a
   *  do lugar que já está no campo. É o sinal para o host descartar um pedido
   *  de "Minha localização" ainda em voo: a escolha manual vence a posição, e
   *  o valor sozinho não distingue reescolher o mesmo lugar de não mexer. */
  onPlacePick?(): void;
};

/**
 * A coluna de UM campo. No layout de página o rótulo da pergunta ("Quem",
 * "Onde", "Quando") fica VISÍVEL em cima do gatilho, e a largura de cada coluna
 * é a do protótipo — QUEM e QUANDO fixos, ONDE com o resto da fileira.
 *
 * O rótulo é `<span aria-hidden>` e não `<label>`: o nome acessível de cada
 * campo já sai do `aria-label` do próprio gatilho (que junta rótulo e valor),
 * então um rótulo associado somaria uma segunda leitura do mesmo texto — e dois
 * dos três campos são botões, que `<label for>` não rotula.
 *
 * O ancestral posicionado dos painéis (`absolute top-full`) segue sendo a caixa
 * do gatilho, nunca a coluna: pendurado na coluna o painel desceria a partir do
 * rótulo, com os 20px dele dentro da conta.
 */
function HotelSearchFieldColumn({
  naked,
  label,
  field,
  children,
}: {
  readonly naked: boolean;
  readonly label: string;
  readonly field: 'rooms' | 'place' | 'dates';
  readonly children: ReactNode;
}): ReactNode {
  /* No card os três campos disputam uma fileira `w-fit` e encolhem juntos; o
     destino é o que carrega texto livre e, sem um piso, era o primeiro a virar
     uma letra e reticências no celular. */
  if (!naked) {
    return (
      <div className={field === 'place' ? 'relative min-w-24' : 'relative min-w-0'}>{children}</div>
    );
  }
  return (
    <div className="hui-hotel-search-page-field" data-field={field}>
      <span aria-hidden="true" className="hui-hotel-search-page-label">
        {label}
      </span>
      <div className="relative min-w-0">{children}</div>
    </div>
  );
}

/** As props do miolo do QUEM, as mesmas no popover, na folha e no cartão da
 *  folha da conversa. */
function roomsFieldProps(
  props: HotelSearchBarProps,
  disabled: boolean,
): Omit<HotelRoomsFieldProps, 'view' | 'onViewChange' | 'fill'> {
  return {
    rooms: props.value.rooms,
    travelerIds: props.value.travelerIds,
    policyTravelerId: props.value.policyTravelerId,
    requesterId: props.requesterId,
    travelers: props.travelers,
    travelerSearchState: props.travelerSearchState,
    hasMoreTravelers: props.hasMoreTravelers,
    copy: props.copy,
    disabled,
    onChange: (next) => {
      props.onChange({ ...props.value, ...next });
    },
    onTravelerQueryChange: (query) => {
      props.onTravelerQueryChange(query);
    },
    onLoadMoreTravelers: () => {
      props.onLoadMoreTravelers();
    },
  };
}

/**
 * A coluna ONDE inteira: o campo de destino e o popover de sugestões que se
 * ancora nele. Componente próprio e não um pedaço do corpo de `HotelSearchBar`
 * porque aquela função vive no teto de linhas e de complexidade do lint — e
 * porque o rascunho de digitação e o popover são um assunto só, que ninguém
 * mais na barra toca.
 *
 * O `Onde` é o `PlaceField` compartilhado NAS DUAS cascas — a mesma
 * pílula-gatilho das praças do aéreo (o `CampoDaPill` do desenho): fechado é
 * botão com glifo de pino, valor e hover; aberto, a busca acontece NO CAMPO e o
 * valor vira a dica (decisão do designer, 2026-08-19). O card compacto ficou
 * numa receita anterior (o `CommandInput` do cmdk, um input fixo com a lupa
 * dele) quando a página migrou, e era essa a divergência com o protótipo.
 *
 * A abertura é da máquina de painéis da barra (um popover por vez), então o
 * par controlado entra sempre.
 *
 * `placeQuery` é estado do PAI e não daqui: o resumo do campo é o rascunho
 * quando ele existe, e é o efeito lá em cima — o que mata o rascunho quando o
 * popover fecha — que garante que ele nunca sobreviva ao popover.
 */
/** O "perto de mim" da linha `MyLocationRow`, repassado embrulhado e não por
 *  referência: `onUseMyLocation` é declarado como MÉTODO no tipo de props, e
 *  passar o método solto cai em `unbound-method`. O ternário preserva a
 *  ausência, que é o que a linha lê pra não existir. */
function myLocationHandler(props: HotelSearchBarProps): (() => void) | undefined {
  return props.onUseMyLocation
    ? () => {
        props.onUseMyLocation?.();
      }
    : undefined;
}

function DestinationColumn({
  props,
  panels,
  naked,
  pageSheet,
  disabled,
  wanted,
  placeQuery,
  setPlaceQuery,
}: {
  readonly props: HotelSearchBarProps;
  readonly panels: HotelSearchPanels;
  readonly naked: boolean;
  readonly pageSheet: boolean;
  readonly disabled: boolean;
  /** Ver `guidedField` — o anel de pendente neste campo. */
  readonly wanted: boolean;
  readonly placeQuery: string | null;
  readonly setPlaceQuery: (query: string | null) => void;
}): ReactNode {
  const { copy, value } = props;
  const { activePanel } = panels;
  const placeTriggerRef = useRef<HTMLButtonElement>(null);
  /* Página ESTREITA (#2653): o `PlaceField` sai de cena e o ONDE vira a mesma
     pílula dos vizinhos abrindo uma folha de tela cheia com a receita do
     cartão da folha da conversa — input em cima, lista embaixo. O pick fecha a
     folha (`onPicked`), como o popover da casca larga fecha no pick; abrir o
     QUANDO é gesto no gatilho dele — ou a continuação deste pick, no
     preenchimento guiado (`afterPick`, folha em folha). */
  if (pageSheet) {
    return (
      <HotelSearchFieldColumn naked={naked} label={copy.whereLabel} field="place">
        <SearchField
          triggerRef={placeTriggerRef}
          icon={MapPinIcon}
          label={copy.destinationLabel}
          text={value.place || copy.destinationPlaceholder}
          muted={!value.place}
          naked={naked}
          wanted={wanted}
          expanded={activePanel === 'places'}
          disabled={disabled}
          onOpen={() => {
            panels.setRequestedPanel('places');
          }}
        />
        <PageFieldSheet
          open={activePanel === 'places'}
          onClose={() => {
            panels.setRequestedPanel(null);
          }}
          // O foco volta à pílula — salvo se a folha do campo seguinte já o tomou.
          returnFocusTo={() => returnFocusUnlessMoved(placeTriggerRef)()}
          title={copy.whereLabel}
          backLabel={copy.adjustSearchLabel}
        >
          <div className="min-h-0 flex-1 overflow-y-auto scrollbar-rail p-4 pr-[calc(1rem-var(--scrollbar-rail))]">
            <SheetPlaceSearch
              props={props}
              panels={panels}
              placeQuery={placeQuery}
              setPlaceQuery={setPlaceQuery}
              onPicked={(next) => {
                panels.afterPick(next, () => {
                  panels.setRequestedPanel(null);
                });
              }}
              fill
            />
          </div>
        </PageFieldSheet>
      </HotelSearchFieldColumn>
    );
  }
  const pickPlace = (place: HotelPlaceSuggestion) => {
    const next = commitPlace(props, place, setPlaceQuery);
    panels.afterPick(next, () => {
      panels.dismissPanel('places', true);
    });
  };
  /* No card não há rótulo visível, então o nome acessível junta rótulo e valor
     (`Destino · São Paulo`), como os gatilhos vizinhos do Quem e do Quando — e
     como os robôs de e2e leem. Na página o rótulo está NA TELA e o nome fica só
     com o assunto do campo. */
  const label = naked
    ? copy.destinationLabel
    : `${copy.destinationLabel} · ${value.place || copy.destinationPlaceholder}`;
  return (
    <HotelSearchFieldColumn naked={naked} label={copy.whereLabel} field="place">
      <PlaceField<HotelPlaceSuggestion>
        label={label}
        {...(value.place ? { value: { title: value.place } } : {})}
        emptyText={copy.destinationPlaceholder}
        icon={<MapPinIcon className="size-3.5" aria-hidden="true" />}
        /* Na cápsula do card a borda é da cápsula: o campo fica `ghost`, como
           Quem e Quando ao lado. Na página cada campo desenha a própria. */
        variant={naked ? 'outline' : 'ghost'}
        disabled={disabled}
        copy={{
          label: copy.destinationLabel,
          emptyLabel: copy.noPlacesLabel,
          errorLabel: copy.placeErrorLabel,
        }}
        search={{
          state: props.placeSearchState,
          suggestions: props.placeSuggestions,
          onQueryChange: (query) => {
            setPlaceQuery(query);
            props.onPlaceQueryChange(query);
          },
        }}
        {...(value.selectedPlaceId !== undefined ? { selectedPlaceId: value.selectedPlaceId } : {})}
        wanted={wanted}
        open={activePanel === 'places'}
        // Nascido aberto pelo guia da página: o input já vem focado.
        openedByGesture={panels.mount === 'places'}
        onOpenChange={(next) => {
          if (next) panels.setRequestedPanel('places');
          else panels.dismissPanel('places', false);
        }}
        onPick={pickPlace}
        header={
          <MyLocationRow
            copy={{ label: copy.myLocationLabel, errorLabel: copy.myLocationErrorLabel }}
            state={props.myLocation?.state}
            query={placeQuery ?? ''}
            onUse={myLocationHandler(props)}
          />
        }
      />
    </HotelSearchFieldColumn>
  );
}

/**
 * A coluna QUEM inteira: o gatilho com o retrato e o painel de quartos — popover
 * na casca larga e na página larga, folha de tela cheia na página estreita.
 * Extraída do corpo de `HotelSearchBar` pelo mesmo teto de complexidade.
 */
function RoomsColumn({
  props,
  panels,
  naked,
  pageSheet,
  disabled,
  wanted,
  roomSummary,
  guest,
}: {
  readonly props: HotelSearchBarProps;
  readonly panels: HotelSearchPanels;
  readonly naked: boolean;
  readonly pageSheet: boolean;
  readonly disabled: boolean;
  /** Ver `guidedField` — o anel de pendente neste campo. */
  readonly wanted: boolean;
  readonly roomSummary: string;
  readonly guest: ReactNode | undefined;
}): ReactNode {
  const open = panels.activePanel === 'rooms';
  const trigger = (ref: RefObject<HTMLButtonElement | null>) => (
    <SearchField
      triggerRef={ref}
      icon={UsersIcon}
      {...(guest ? { glyph: guest } : {})}
      label={props.copy.roomsLabel}
      text={guest ? props.copy.guestCountLabel(namedGuestCount(props.value)) : roomSummary}
      accessibleText={roomSummary}
      naked={naked}
      wanted={wanted}
      expanded={open}
      disabled={disabled}
      onOpen={() => {
        panels.setRequestedPanel('rooms');
      }}
    />
  );
  return (
    <HotelSearchFieldColumn naked={naked} label={props.copy.whoLabel} field="rooms">
      <HotelRoomsPickerField
        open={open}
        onOpenChange={(next) => {
          if (next) panels.setRequestedPanel('rooms');
          else panels.dismissPanel('rooms', false);
        }}
        layout={pageSheet ? 'sheet' : 'popover'}
        title={pageSheet ? props.copy.whoLabel : props.copy.roomsLabel}
        backLabel={props.copy.adjustSearchLabel}
        triggerRef={panels.roomsTriggerRef}
        // Na folha o foco volta à pílula — salvo se a folha seguinte já o tomou.
        returnFocusTo={returnFocusUnlessMoved(panels.roomsTriggerRef)}
        trigger={trigger}
        summary={roomSummary}
        // O «Pronto» é resposta do campo: no preenchimento guiado segue para o
        // próximo pendente (Onde, Quando) ou pousa o foco no Buscar.
        onDone={() => {
          panels.afterPick(props.value, () => {
            panels.dismissPanel('rooms', true);
          });
        }}
        field={roomsFieldProps(props, disabled)}
      />
    </HotelSearchFieldColumn>
  );
}

/**
 * A coluna QUANDO inteira: o gatilho do intervalo e o calendário de dois meses
 * que abre embaixo dele. Extraída pelo mesmo motivo que a coluna ONDE.
 */
function DatesColumn({
  props,
  panels,
  naked,
  pageSheet,
  disabled,
  wanted,
}: {
  readonly props: HotelSearchBarProps;
  readonly panels: HotelSearchPanels;
  readonly naked: boolean;
  readonly pageSheet: boolean;
  readonly disabled: boolean;
  /** Ver `guidedField` — o anel de pendente neste campo. */
  readonly wanted: boolean;
}): ReactNode {
  const { copy, value, dateSummary, dateAccessibleSummary } = props;
  const { activePanel } = panels;
  const confirmStay = useStayConfirm(props, panels, disabled);
  /* O QUANDO vazio troca de copy na página: o resumo que o host manda já vem
     resolvido com "A definir" (a pílula estreita do card), e aqui cabe — e o
     desenho pede — a pergunta inteira. Datas escolhidas seguem sendo o resumo
     do host nos dois layouts. */
  const dateText = naked && !value.checkin ? copy.datesPlaceholder : dateSummary;
  /* Na página ESTREITA (a casca de card do celular, que não tem a folha da
     conversa) as datas abrem numa folha própria em vez do popover: 29rem de
     dois meses sob o campo viravam rolo horizontal com o `Confirmar` fora da
     tela, e um mês só obrigava a paginar pra ver uma estadia que cruza a
     virada. A folha empilha a janela de meses e prega o Confirmar embaixo
     (`PageFieldSheet`) — só a pele; a máquina de painéis segue a mesma
     (`activePanel === 'dates'`). Na conversa estreita esta coluna nem monta
     (pílula + `HotelSearchSheet`). */
  return (
    <HotelSearchFieldColumn naked={naked} label={copy.whenLabel} field="dates">
      <SearchField
        triggerRef={panels.datesTriggerRef}
        icon={CalendarDotsIcon}
        label={copy.datesLabel}
        text={dateText}
        {...(dateAccessibleSummary ? { accessibleText: dateAccessibleSummary } : {})}
        muted={!value.checkin}
        naked={naked}
        wanted={wanted}
        expanded={activePanel === 'dates'}
        disabled={disabled}
        onOpen={() => {
          panels.setRequestedPanel('dates');
        }}
      />
      {pageSheet ? (
        <PageFieldSheet
          open={activePanel === 'dates'}
          onClose={() => {
            panels.setRequestedPanel(null);
          }}
          // Foco já pousado na barra (o Confirmar mandou-o ao Buscar, ou ao
          // próprio gatilho): fica. Fechou de dentro da folha (voltar, véu,
          // Escape): volta ao gatilho das datas.
          returnFocusTo={() =>
            panels.rootRef.current?.contains(document.activeElement)
              ? false
              : (panels.datesTriggerRef.current ?? false)
          }
          title={copy.whenLabel}
          backLabel={copy.adjustSearchLabel}
        >
          <StayCalendar
            copy={copy}
            initial={{ checkin: value.checkin, checkout: value.checkout }}
            months={2}
            layout="sheet"
            searches={(dates) => staySearches(props, disabled, dates)}
            onConfirm={confirmStay}
          />
        </PageFieldSheet>
      ) : (
        activePanel === 'dates' && (
          <div
            className="absolute top-full left-1/2 z-50 mt-2 w-[29rem] max-w-[calc(100vw-2rem)] -translate-x-1/2 overflow-x-auto rounded-xl bg-popover p-2 shadow-lg ring-1 ring-foreground/10"
            role="dialog"
            aria-label={copy.datesLabel}
          >
            <StayCalendar
              copy={copy}
              initial={{ checkin: value.checkin, checkout: value.checkout }}
              months={2}
              searches={(dates) => staySearches(props, disabled, dates)}
              onConfirm={confirmStay}
            />
          </div>
        )
      )}
    </HotelSearchFieldColumn>
  );
}

/**
 * O ONDE dentro do cartão da folha: input em cima, lista embaixo — a mesma
 * receita do popup do `PlaceField` (o `PlaceCommand` ligando input e lista, a
 * linha "perto de mim" só sem termo), sem o popover, porque aqui o cartão já é
 * a superfície.
 *
 * Escolher um lugar NÃO avança pro QUANDO: no cartão da folha da conversa o
 * pick só grava (fechar seria derrubar a folha inteira, e abrir outro cartão
 * seria um gesto que ninguém fez); na folha de campo da página o host fecha a
 * folha por `onPicked`, como o popover da casca larga fecha no pick.
 */
function SheetPlaceSearch({
  props,
  panels,
  placeQuery,
  setPlaceQuery,
  onPicked,
  fill = false,
}: {
  readonly props: HotelSearchBarProps;
  readonly panels: HotelSearchPanels;
  readonly placeQuery: string | null;
  readonly setPlaceQuery: (query: string | null) => void;
  /** O que fazer DEPOIS de gravar o lugar, com o valor recém-gravado —
   *  ausente ⇒ nada (o cartão fica). */
  readonly onPicked?: (next: HotelSearchValue) => void;
  /** Na folha de tela cheia da página a lista toma a altura que houver; no
   *  cartão da folha da conversa ela tem teto, porque os outros cartões
   *  precisam continuar à vista. */
  readonly fill?: boolean;
}): ReactNode {
  const { copy, value } = props;
  const panelRef = useRef<HTMLDivElement>(null);
  const term = placeQuery ?? '';
  const query = term.trim();
  const pick = (place: HotelPlaceSuggestion) => {
    const next = commitPlace(props, place, setPlaceQuery);
    onPicked?.(next);
  };
  return (
    <div className="grid gap-2">
      <div className="flex h-11 items-center gap-2 rounded-xl border border-input px-3 transition-colors focus-within:border-ring focus-within:ring-3 focus-within:ring-ring/50">
        <MagnifyingGlassIcon className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        <PlaceSearchInput
          term={term}
          onTerm={(next) => {
            setPlaceQuery(next);
            props.onPlaceQueryChange(next);
          }}
          placeholder={value.place || copy.destinationPlaceholder}
          panelRef={panelRef}
          label={copy.destinationLabel}
          /* Expandir o cartão É um gesto (toque no cartão ou na pílula), então o
             cursor pode vir junto — e no celular é o teclado subindo. */
          autoFocus
          onEscape={() => {
            panels.setRequestedPanel(null);
          }}
        />
      </div>
      <div ref={panelRef} className="contents">
        <PlaceCommand
          query={query}
          suggestions={props.placeSuggestions}
          selectedPlaceId={value.selectedPlaceId}
          label={copy.destinationLabel}
          className="bg-transparent p-0"
        >
          <CommandList className={fill ? 'max-h-none' : 'max-h-64'}>
            <MyLocationRow
              copy={{ label: copy.myLocationLabel, errorLabel: copy.myLocationErrorLabel }}
              state={props.myLocation?.state}
              query={term}
              onUse={myLocationHandler(props)}
            />
            <PlaceResults
              copy={{
                label: copy.destinationLabel,
                emptyLabel: copy.noPlacesLabel,
                errorLabel: copy.placeErrorLabel,
              }}
              state={props.placeSearchState}
              suggestions={props.placeSuggestions}
              selectedPlaceId={value.selectedPlaceId}
              query={query}
              onPick={pick}
            />
          </CommandList>
        </PlaceCommand>
      </div>
    </div>
  );
}

/**
 * O palco ESTREITO: pílula-resumo + folha (ver `hotel_search_sheet.tsx`). A
 * máquina de painéis é a MESMA da casca larga — `activePanel` vira "qual
 * cartão está aberto", e `null` é a folha fechada. Por isso o Confirmar das
 * datas (fecha e busca) vale aqui sem código próprio.
 */
function CompactHotelSearch({
  props,
  panels,
  disabled,
  placeQuery,
  setPlaceQuery,
  roomSummary,
  action,
}: {
  readonly props: HotelSearchBarProps;
  readonly panels: HotelSearchPanels;
  readonly disabled: boolean;
  readonly placeQuery: string | null;
  readonly setPlaceQuery: (query: string | null) => void;
  readonly roomSummary: string;
  readonly action: ReactNode;
}): ReactNode {
  const { copy, value, dateSummary } = props;
  const { activePanel } = panels;
  const confirmStay = useStayConfirm(props, panels, disabled);
  const summaryRef = useRef<HTMLButtonElement>(null);
  const cards: readonly HotelSearchSheetCard[] = [
    {
      field: 'rooms',
      label: copy.whoLabel,
      value: roomSummary,
      triggerRef: panels.roomsTriggerRef,
      content: <HotelRoomsField {...roomsFieldProps(props, disabled)} fill />,
    },
    {
      field: 'places',
      label: copy.whereLabel,
      value: value.place || copy.destinationPlaceholder,
      muted: !value.place,
      content: (
        <SheetPlaceSearch
          props={props}
          panels={panels}
          placeQuery={placeQuery}
          setPlaceQuery={setPlaceQuery}
        />
      ),
    },
    {
      field: 'dates',
      label: copy.whenLabel,
      value: dateSummary,
      muted: !value.checkin,
      triggerRef: panels.datesTriggerRef,
      content: (
        <StayCalendar
          copy={copy}
          initial={{ checkin: value.checkin, checkout: value.checkout }}
          months={1}
          searches={(dates) => staySearches(props, disabled, dates)}
          onConfirm={confirmStay}
        />
      ),
    },
  ];
  return (
    <>
      <HotelSearchSummary
        rootRef={panels.rootRef}
        triggerRef={summaryRef}
        title={value.place || copy.destinationPlaceholder}
        caption={`${dateSummary} · ${roomSummary}`}
        muted={!value.place}
        expanded={activePanel !== null}
        disabled={disabled}
        /* A folha abre no primeiro campo PENDENTE — a mesma fila da casca larga
           — e, com tudo respondido, no destino, que é o critério que mais muda. */
        onOpen={() => {
          panels.setRequestedPanel(pendingHotelSearchFields(value)[0] ?? 'places');
        }}
        action={action}
      />
      <HotelSearchSheet
        open={activePanel !== null}
        onOpenChange={(open) => {
          if (!open) panels.setRequestedPanel(null);
        }}
        returnFocusTo={summaryRef}
        title={copy.adjustSearchLabel}
        cards={cards}
        activeField={activePanel}
        onActivate={panels.setRequestedPanel}
        footer={
          <Button
            size="lg"
            type="button"
            {...searchCta(props, panels, disabled, () => {
              panels.setRequestedPanel(null);
              props.onSubmit(value);
            })}
          >
            <MagnifyingGlassIcon data-icon="inline-start" aria-hidden="true" />
            {copy.searchLabel}
          </Button>
        }
      />
    </>
  );
}

/**
 * A barra de busca de hotel — QUEM · ONDE · QUANDO e o disparo — nos dois
 * palcos em que ela existe, com a MESMA mecânica de painéis nos dois.
 *
 * `naked` ausente (default) é o palco da CONVERSA: uma pílula-card com fundo,
 * anel e sombra, que flutua sobre o mapa do resultado; os rótulos ficam só no
 * nome acessível e o disparo é a lupa. `naked` é o palco da PÁGINA (o passo de
 * busca determinístico): sem casca, cada campo com o rótulo da pergunta visível
 * em cima, largura do desenho, o Buscar dizendo o próprio nome e a garantia de
 * política fechando o bloco.
 *
 * O que NÃO muda entre os dois: os três painéis, o rascunho de digitação do
 * destino e a ordem de foco. O layout é pele; o gesto é um só.
 */
export function HotelSearchBar(props: HotelSearchBarProps) {
  const { value, copy, readOnly = false, busy = false, naked = false } = props;
  const disabled = readOnly || busy;
  /* Dois palcos estreitos: na CONVERSA a barra vira pílula + folha com os
     três cartões; na PÁGINA (`naked`) os campos já empilham por consulta de
     contêiner, e cada um abre a própria folha de tela cheia (#2653). */
  const narrow = useMediaQuery(HOTEL_NARROW_QUERY);
  const sheet = narrow && !naked;
  const pageSheet = narrow && naked;
  // `narrow` É "há folha em cena" (uma das duas): os painéis não flutuam.
  // Nenhum painel abre SOZINHO ao responder um campo: a abertura é gesto no
  // gatilho do próprio campo ou, com `guided`, a continuação de um pick
  // (`afterPick`) — e, na fileira larga, o primeiro pendente ao nascer.
  const guide = guidedField(props, disabled, narrow);
  const panels = useHotelSearchPanels(disabled, narrow, guide);
  const { activePanel, guided, setRequestedPanel, rootRef, searchTriggerRef } = panels;
  const roomSummary = copy.roomSummary(Math.max(1, value.rooms.length), namedGuestCount(value));
  const guest = naked ? guestGlyph(value, props.travelers, props.requesterId) : undefined;

  const [placeQuery, setPlaceQuery] = usePlaceDraft(activePanel);

  const searchButton = (
    <Button
      ref={searchTriggerRef}
      /* Na PÁGINA estreita (a coluna de campos: abaixo da régua `sm` do DS no
         contêiner da página, o mesmo degrau do CSS dela) o Buscar fecha a coluna
         em largura cheia e 44px, como o desenho da #2647 — no card e na fileira
         larga segue a pílula à direita. */
      className={
        naked
          ? 'h-9 self-end rounded-full px-4 @max-[theme(--breakpoint-sm)]:h-11 @max-[theme(--breakpoint-sm)]:w-full @max-[theme(--breakpoint-sm)]:self-stretch'
          : 'h-9 self-end rounded-full px-4'
      }
      type="button"
      {...searchCta(props, { guided, setRequestedPanel }, disabled, () => {
        props.onSubmit(value);
      })}
      /* Na página o rótulo está NA TELA, e um `aria-label` com o mesmo texto
         só duplicaria a leitura; no card o botão é só a lupa e é ele que
         carrega o nome. */
      {...(naked ? {} : { 'aria-label': copy.searchLabel })}
    >
      <MagnifyingGlassIcon />
      {naked && copy.searchLabel}
    </Button>
  );

  if (sheet) {
    return (
      <CompactHotelSearch
        props={props}
        panels={panels}
        disabled={disabled}
        placeQuery={placeQuery}
        setPlaceQuery={setPlaceQuery}
        roomSummary={roomSummary}
        action={searchButton}
      />
    );
  }

  const fields = (
    <>
      <RoomsColumn
        props={props}
        panels={panels}
        naked={naked}
        pageSheet={pageSheet}
        disabled={disabled}
        wanted={guide.wanted === 'rooms'}
        roomSummary={roomSummary}
        guest={guest}
      />
      <DestinationColumn
        props={props}
        panels={panels}
        naked={naked}
        pageSheet={pageSheet}
        disabled={disabled}
        wanted={guide.wanted === 'places'}
        placeQuery={placeQuery}
        setPlaceQuery={setPlaceQuery}
      />
      <DatesColumn
        props={props}
        panels={panels}
        naked={naked}
        pageSheet={pageSheet}
        disabled={disabled}
        wanted={guide.wanted === 'dates'}
      />
      {searchButton}
    </>
  );

  if (naked) {
    return (
      <div className="hui-hotel-search-page">
        <div ref={rootRef} className="hui-hotel-search-page-row">
          {fields}
        </div>
        {/* A linha "Sua política de viagem" que fechava o bloco no protótipo
            está FORA de cena (#2655): ela promete uma modal de políticas que
            ainda não existe. Volta quando a modal existir — a copy
            (`policyHintLabel`) continua no contrato por isso. */}
      </div>
    );
  }
  return (
    <div
      ref={rootRef}
      className="relative flex w-fit max-w-full flex-nowrap items-end gap-2 rounded-full bg-card/95 p-1.5 shadow-md ring-1 ring-foreground/10 backdrop-blur-sm"
    >
      {fields}
    </div>
  );
}
